import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { TabsContent } from '@/components/ui/tabs';
import { useEvaluationInsights } from '@/modules/simulations/hooks/useSimulations';
import type { EvaluationWithSimulation } from '@/modules/simulations/types';
import { isEvaluationInProgress } from '@/modules/simulations/utils';

import { InsightRow } from './InsightRow';

interface InsightsTabProps {
  evaluation: EvaluationWithSimulation;
}

export const InsightsTab = ({ evaluation }: InsightsTabProps) => {
  const { t } = useTranslation();
  const { mutate, data, isPending, isError, error, reset } = useEvaluationInsights(
    evaluation.brain_id,
    evaluation.run_id,
    evaluation.evaluation_id
  );

  const handleGenerate = () => {
    if (isError) {
      reset();
    }
    mutate();
  };

  const insights = data?.insights ?? [];
  const hasResults = data !== undefined && insights.length > 0;
  const isEmptyResult = data !== undefined && insights.length === 0;
  const evaluationInProgress = isEvaluationInProgress(evaluation.status);

  return (
    <TabsContent value="insights" className="flex-1 space-y-4 overflow-y-auto p-4">
      {evaluationInProgress ? null : (
        <div className="flex flex-col gap-4">
          {!hasResults && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleGenerate}
              isLoading={isPending}
              className="self-start"
            >
              {t('simulations.insights_generate')}
            </Button>
          )}

          {isError && (
            <p className="text-xs leading-4 text-destructive">
              {error?.message || t('simulations.insights_error_generic')}
            </p>
          )}

          {isEmptyResult && (
            <p className="text-sm leading-5 text-muted-foreground">
              {t('insights.no_insights_found')}
            </p>
          )}

          {hasResults && (
            <div className="flex flex-col gap-0.5">
              <p className="px-1 text-xs leading-4 text-muted-foreground">{t('insights.title')}</p>
              <div className="overflow-clip rounded-lg border border-border bg-background">
                {insights.map((insight) => (
                  <InsightRow key={insight.insight_id} insight={insight} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </TabsContent>
  );
};
